import { FunctionComponent } from 'preact';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'preact/hooks';
import { playerDataSelector } from '../../../common/store/selectors/playerData.selector';
import { useStyles } from '../../../common/hooks/useStyles';
import { getReadMoreStyleObject } from './readMore.style';
import { isEmpty, shouldShowReadMore } from '../../../common/utils/utils';
import { getButtonIconId, getButtonTextId } from '../htmlElements/htmlElementsGetter';
import { Icon } from '../../../common/components/icon/icon';
import { READ_MORE_ICON_TYPES } from '../icon/iconMapper';
import { adsDataSelector } from '../../../common/store/selectors/adsData.selector';
import { css } from '../../../common/styles/styles.utils';
import { splitViewSelector } from '../../../common/store/selectors/splitView.selector';
import { setUserInteraction } from '../store/readMore.actions';
import { useHoverRef } from '../../../common/hooks/useHoverRef';
import { ErrorBoundary } from '../../../common/components/errorBoundary/errorBoundary';

const READ_MORE_PLUGIN = 'ReadMore';

const TIME_SEGMENTS_INDEX = 0;
const URL_ADDRESSES_INDEX = 1;

const updateCurrentSceneOnVideoProgress = (setCurrentScene, setStartOfCurrentScene, setEndOfCurrentScene, currentScene,
  sortedCustomParams): void => {
  setCurrentScene(currentScene + 1);
  setStartOfCurrentScene(currentScene === -1 ? 0 : sortedCustomParams[currentScene][TIME_SEGMENTS_INDEX]);
  setEndOfCurrentScene(sortedCustomParams[currentScene + 1][TIME_SEGMENTS_INDEX]);
};

const updateCurrentSceneOnSeeking = (setCurrentScene, setStartOfCurrentScene, setEndOfCurrentScene, sortedCustomParams,
  startOfCurrentScene, currentVideoTime): void => {
  const scenesTimeSegments = sortedCustomParams.map(sortedCustomParam => sortedCustomParam[TIME_SEGMENTS_INDEX]);
  scenesTimeSegments.unshift(0);
  for (let i = 0; i < scenesTimeSegments.length - 1; i += 1) {
    if (scenesTimeSegments[i] < currentVideoTime && currentVideoTime < scenesTimeSegments[i + 1]) {
      setStartOfCurrentScene(scenesTimeSegments[i]);
      setEndOfCurrentScene(scenesTimeSegments[i + 1]);
      setCurrentScene(i);
    }
  }
};

const resetCurrentSceneData = (setCurrentScene, setStartOfCurrentScene, setEndOfCurrentScene): void => {
  setCurrentScene(-1);
  setEndOfCurrentScene(-1);
  setStartOfCurrentScene(-1);
};

interface ReadMoreProps {
  sortedCustomParams: Array<any>;
}

export const ReadMore: FunctionComponent<ReadMoreProps> = props => {
  const {sortedCustomParams} = props
  const dispatch = useDispatch();
  const playerUniqId = useSelector(playerDataSelector.playerInstanceUniqId);
  const currentVideoTime = useSelector(playerDataSelector.currentVideoTime);
  const videoTagStatus = useSelector(playerDataSelector.videoTagStatus);
  const adStatus = useSelector(adsDataSelector.adStatus);
  const splitViewRatio = useSelector(splitViewSelector.splitViewRatio);
  const playerMode = useSelector(playerDataSelector.playerMode);

  const [articleUrl, setArticleUrl] = useState('');
  const [currentScene, setCurrentScene] = useState(-1);
  const [endOfCurrentScene, setEndOfCurrentScene] = useState(-1);
  const [startOfCurrentScene, setStartOfCurrentScene] = useState(-1);
  const [isVideoCompleted, setVideoCompleted] = useState(false);
  const [buttonRef, buttonHovered] = useHoverRef();

  const startOfNewVideo = currentVideoTime === 0;
  const videoStatusSeeking = videoTagStatus === 'seeking';
  const adPlaying = adStatus === 'playing' || adStatus === 'paused';
  const showReadMore = shouldShowReadMore(splitViewRatio, sortedCustomParams);
  const mobilePlayerMode = playerMode === 'mobile' || playerMode === 'miniMobile' || playerMode === 'stickyMobile';

  const styles = useStyles(getReadMoreStyleObject, [adPlaying, isVideoCompleted, buttonHovered, mobilePlayerMode]);
  const { readMoreContainerStyle, readMoreButtonStyle, readMoreTextStyle, readMoreIconStyle } = styles;

  useEffect(() => {
    if (videoTagStatus === 'complete') {
      setVideoCompleted(true);
    }
  }, [videoTagStatus]);

  useEffect(() => {
    if (startOfNewVideo && isVideoCompleted) {
      setVideoCompleted(false);
    }
  }, [startOfNewVideo, isVideoCompleted]);

  useEffect(() => {
    if (!isEmpty(sortedCustomParams)) {
      if (!videoStatusSeeking && !isVideoCompleted && currentVideoTime >= endOfCurrentScene && currentScene < sortedCustomParams.length - 1) {
        updateCurrentSceneOnVideoProgress(setCurrentScene, setStartOfCurrentScene, setEndOfCurrentScene, currentScene, sortedCustomParams);
      } else if (videoStatusSeeking && !isVideoCompleted) {
        updateCurrentSceneOnSeeking(setCurrentScene, setStartOfCurrentScene, setEndOfCurrentScene, sortedCustomParams,
          startOfCurrentScene, currentVideoTime);
      } else if (isVideoCompleted && currentScene === sortedCustomParams.length - 1) {
        resetCurrentSceneData(setCurrentScene, setEndOfCurrentScene, setStartOfCurrentScene);
      }
    }
  }, [currentVideoTime, endOfCurrentScene, currentScene, sortedCustomParams, isVideoCompleted, startOfCurrentScene, videoStatusSeeking]);

  useEffect(() => {
    if (!isEmpty(sortedCustomParams[currentScene])) {
      setArticleUrl(sortedCustomParams[currentScene][URL_ADDRESSES_INDEX]);
    }
  }, [currentScene, sortedCustomParams, setArticleUrl]);

  const onReadMoreClicked = (): void => {
    dispatch(setUserInteraction('recirculationArticle'));
    if (!isEmpty(articleUrl)) {
      window.open(articleUrl, '_blank');
    }
  };

  return (
    <ErrorBoundary pluginName={READ_MORE_PLUGIN}>
      {
        showReadMore ?
          (
            <div style={css(readMoreContainerStyle)} onClick={(e: MouseEvent): void => e.stopPropagation()}>
              <div style={css(readMoreButtonStyle)} onClick={onReadMoreClicked} ref={buttonRef}>
                <span style={css(readMoreTextStyle)} id={getButtonTextId(playerUniqId)}>
                  Read More
                </span>
                <Icon
                  icon={READ_MORE_ICON_TYPES.READ_MORE_ARROW}
                  style={readMoreIconStyle}
                  id={getButtonIconId(playerUniqId)}
                  onClick={onReadMoreClicked}
                />
              </div>
            </div>
          ) : null
      }
    </ErrorBoundary>
  );
};
